import React, { useState } from 'react';
import '../../styles/character-guide-modal.css';

const CharacterGuideModal = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to Your Character",
      icon: "⚔️",
      content: (
        <>
          <h3>Character Overview</h3>
          <p>Your character is the hero who explores dungeons, fights monsters, and grows stronger through equipment and progression.</p>

          <div className="cg-info-box">
            <h4>🎯 Key Features</h4>
            <ul>
              <li><strong>Level System:</strong> Gain experience from defeating dungeon enemies</li>
              <li><strong>Equipment Slots:</strong> Equip weapons, armor, and accessories for stat bonuses</li>
              <li><strong>Inventory Management:</strong> Store and organize items, materials, and equipment</li>
              <li><strong>Combat Stats:</strong> View your offensive and defensive capabilities</li>
            </ul>
          </div>

          <div className="cg-highlight-box">
            <p>💡 <strong>Tip:</strong> Your character's power directly affects your success in dungeons. Better equipment = stronger character!</p>
          </div>
        </>
      )
    },
    {
      title: "Equipment & Stats",
      icon: "🛡️",
      content: (
        <>
          <h3>Equipment System</h3>
          <p>Equipment provides stat bonuses that enhance your character's combat abilities.</p>

          <div className="cg-table-wrapper">
            <table className="cg-table">
              <thead>
                <tr>
                  <th>Equipment Slot</th>
                  <th>Typical Stats</th>
                  <th>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>⚔️ Weapon</strong></td>
                  <td>Attack, Crit Chance</td>
                  <td>Primary damage source</td>
                </tr>
                <tr>
                  <td><strong>🛡️ Armor</strong></td>
                  <td>Defense, Health</td>
                  <td>Reduces damage taken</td>
                </tr>
                <tr>
                  <td><strong>💍 Accessory</strong></td>
                  <td>Various bonuses</td>
                  <td>Specialized effects</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4>How to Equip Items</h4>
          <ol>
            <li><strong>Drag & Drop:</strong> Drag equipment from your inventory to the equipment slot</li>
            <li><strong>Right-Click:</strong> Right-click an item in your inventory for a context menu</li>
            <li><strong>Auto-Equip:</strong> Better items can be auto-equipped when picked up</li>
          </ol>

          <div className="cg-info-box">
            <h4>📊 Understanding Stats</h4>
            <p><strong>Primary Stats (Left Panel):</strong></p>
            <ul>
              <li><strong>Level:</strong> Your character's experience level</li>
              <li><strong>HP:</strong> Current / Maximum health</li>
              <li><strong>Experience:</strong> Progress toward next level</li>
            </ul>

            <p><strong>Combat Stats (Character Tab):</strong></p>
            <ul>
              <li><strong>Attack:</strong> Base damage per hit</li>
              <li><strong>Defense:</strong> Damage reduction</li>
              <li><strong>Crit Chance:</strong> % chance to deal critical damage</li>
              <li><strong>Crit Damage:</strong> Multiplier for critical hits (e.g., 1.5× = 150% damage)</li>
              <li><strong>Attack Speed:</strong> How fast you attack</li>
            </ul>
          </div>
        </>
      )
    },
    {
      title: "Inventory Management",
      icon: "🎒",
      content: (
        <>
          <h3>Your Inventory</h3>
          <p>The inventory stores all items you collect from dungeons, including equipment, consumables, and materials.</p>

          <div className="cg-info-box">
            <h4>📦 Inventory Features</h4>
            <ul>
              <li><strong>Grid Layout:</strong> Items are organized in a visual grid</li>
              <li><strong>Drag & Drop:</strong> Reorganize items by dragging them to new positions</li>
              <li><strong>Stacking:</strong> Materials and consumables stack automatically (e.g., "Bone x25")</li>
              <li><strong>Equipment:</strong> Each piece of equipment takes one inventory slot</li>
              <li><strong>Tooltips:</strong> Hover over items to see detailed information</li>
            </ul>
          </div>

          <h4>Item Types</h4>
          <div className="cg-table-wrapper">
            <table className="cg-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Icon Example</th>
                  <th>Stacks?</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Equipment</strong></td>
                  <td>⚔️ 🛡️ 💍</td>
                  <td>No</td>
                  <td>Weapons, armor, accessories - each has unique stats</td>
                </tr>
                <tr>
                  <td><strong>Materials</strong></td>
                  <td>🦴 🧳 ✨</td>
                  <td>Yes</td>
                  <td>Dungeon resources used for crafting and research</td>
                </tr>
                <tr>
                  <td><strong>Consumables</strong></td>
                  <td>🧪 📿</td>
                  <td>Yes</td>
                  <td>Potions and temporary buffs</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="cg-highlight-box">
            <p>💡 <strong>Tip:</strong> Materials like Bone (🦴) are used in certain technology research! Keep them in your inventory.</p>
          </div>
        </>
      )
    },
    {
      title: "Dungeons & Progression",
      icon: "🏰",
      content: (
        <>
          <h3>Dungeon System</h3>
          <p>Dungeons are special tiles on the World Map that contain enemies, treasure, and valuable materials.</p>

          <div className="cg-info-box">
            <h4>🗺️ Finding Dungeons</h4>
            <ul>
              <li><strong>Location:</strong> Dungeons appear as special biomes on the World Map</li>
              <li><strong>Visual:</strong> Highlighted with a <span style={{color: '#a060d0'}}>purple border</span> when discovered</li>
              <li><strong>Tiers:</strong> Dungeons have difficulty tiers (T1, T2, T3) matching your progression</li>
              <li><strong>Entry:</strong> Click on a dungeon tile and select "Enter Dungeon"</li>
            </ul>
          </div>

          <h4>Dungeon Rewards</h4>
          <div className="cg-table-wrapper">
            <table className="cg-table">
              <thead>
                <tr>
                  <th>Reward Type</th>
                  <th>Where to Find</th>
                  <th>Use</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Equipment</strong></td>
                  <td>Enemy drops, treasure chests</td>
                  <td>Equip to increase stats</td>
                </tr>
                <tr>
                  <td><strong>Materials</strong></td>
                  <td>Enemy drops, boss rewards</td>
                  <td>Technology research, crafting</td>
                </tr>
                <tr>
                  <td><strong>Experience</strong></td>
                  <td>Defeating enemies</td>
                  <td>Level up your character</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4>Dungeon Materials & Research</h4>
          <p>Some materials are <strong>only found in dungeons</strong> and are required for advanced technologies:</p>

          <div className="cg-highlight-box">
            <p><strong>Example:</strong> The "Necromancy" technology (Era 2) requires <strong>25 Bone 🦴</strong> from dungeons.</p>
            <p>Without dungeon materials, you cannot progress past certain eras!</p>
          </div>

          <div className="cg-info-box">
            <h4>📊 Tracking Dungeon Materials</h4>
            <p>Dungeon materials can be viewed in two places:</p>
            <ul>
              <li><strong>Resource Panel:</strong> Shows "Dungeon" category with all dungeon materials</li>
              <li><strong>Inventory:</strong> Materials appear as stackable items (e.g., "Bone x25")</li>
            </ul>
          </div>
        </>
      )
    },
    {
      title: "Combat & Strategy",
      icon: "⚔️",
      content: (
        <>
          <h3>Combat Mechanics</h3>
          <p>Combat in dungeons is automatic, but your stats determine the outcome.</p>

          <div className="cg-info-box">
            <h4>⚔️ How Combat Works</h4>
            <ul>
              <li><strong>Auto-Battle:</strong> Combat happens automatically based on your stats</li>
              <li><strong>Attack Speed:</strong> Determines how often you attack</li>
              <li><strong>Damage Calculation:</strong> (Attack - Enemy Defense × 0.5) × Crit Multiplier</li>
              <li><strong>Defense:</strong> Reduces incoming damage by 50% of defense value</li>
              <li><strong>Critical Hits:</strong> Random chance to deal bonus damage</li>
            </ul>
          </div>

          <h4>Combat Log</h4>
          <p>View combat details in the <strong>Dungeon Panel</strong> (left side when in a dungeon):</p>
          <ul>
            <li>Your attacks and damage dealt</li>
            <li>Enemy attacks and damage taken</li>
            <li>Critical hit notifications</li>
            <li>Victory/defeat messages</li>
          </ul>

          <div className="cg-highlight-box">
            <p>💡 <strong>Strategy Tip:</strong> Check the recommended level before entering a dungeon. Going in under-leveled or under-equipped can lead to defeat!</p>
          </div>

          <h4>Death & Consequences</h4>
          <p>If your HP reaches 0:</p>
          <ul>
            <li>You are ejected from the dungeon</li>
            <li>HP is restored to full</li>
            <li>Partial loot is kept (some items may be lost)</li>
            <li>No permanent consequences - try again when stronger!</li>
          </ul>
        </>
      )
    },
    {
      title: "Quick Reference",
      icon: "📖",
      content: (
        <>
          <h3>Character Tab Quick Reference</h3>

          <div className="cg-table-wrapper">
            <table className="cg-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>How To</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>View Stats</strong></td>
                  <td>Open Character tab - stats displayed on left panel</td>
                </tr>
                <tr>
                  <td><strong>Equip Item</strong></td>
                  <td>Drag from inventory to equipment slot OR right-click item</td>
                </tr>
                <tr>
                  <td><strong>Unequip Item</strong></td>
                  <td>Click equipped item to move it back to inventory</td>
                </tr>
                <tr>
                  <td><strong>View Item Details</strong></td>
                  <td>Hover over any item for tooltip with full stats</td>
                </tr>
                <tr>
                  <td><strong>Find Dungeons</strong></td>
                  <td>Explore World Map - dungeons have purple borders</td>
                </tr>
                <tr>
                  <td><strong>Enter Dungeon</strong></td>
                  <td>Click dungeon tile → "Enter Dungeon" button</td>
                </tr>
                <tr>
                  <td><strong>Check Materials</strong></td>
                  <td>Resource Panel (Dungeon category) OR Inventory</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="cg-highlight-box">
            <p>🎯 <strong>Ready to Adventure!</strong> You now know how to manage your character, equip items, and conquer dungeons. Good luck, adventurer!</p>
          </div>

          <div className="cg-info-box">
            <p><strong>Need More Help?</strong> Click the 📖 Guide button in the top navigation for the full Civilopedia with detailed information on all game systems.</p>
          </div>
        </>
      )
    }
  ];

  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="modal-overlay cg-overlay" onClick={handleClose}>
      <div className="cg-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cg-header">
          <div className="cg-header-left">
            <span className="cg-icon">{currentStepData.icon}</span>
            <h2>{currentStepData.title}</h2>
          </div>
          <button className="cg-close-btn" onClick={handleClose}>✕</button>
        </div>

        {/* Progress Indicator */}
        <div className="cg-progress">
          <div className="cg-progress-bar">
            <div
              className="cg-progress-fill"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
          <span className="cg-progress-text">
            Step {currentStep + 1} of {steps.length}
          </span>
        </div>

        {/* Content */}
        <div className="cg-content">
          {currentStepData.content}
        </div>

        {/* Navigation */}
        <div className="cg-footer">
          <button
            className="cg-btn cg-btn-secondary"
            onClick={handlePrev}
            disabled={isFirstStep}
          >
            ← Previous
          </button>

          <div className="cg-dots">
            {steps.map((_, index) => (
              <span
                key={index}
                className={`cg-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                onClick={() => setCurrentStep(index)}
              />
            ))}
          </div>

          {!isLastStep ? (
            <button className="cg-btn cg-btn-primary" onClick={handleNext}>
              Next →
            </button>
          ) : (
            <button className="cg-btn cg-btn-success" onClick={handleClose}>
              Got It! ✓
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CharacterGuideModal;
